import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { Formik, Form, Field } from "formik";

import {
  getFacilities,
  getPatientRecords,
  createPatientRecord,
} from "../../../service/endpoint-manager";
import styles from "../../../styles.module.css";
import clsx from "clsx";

import { useSnackbar } from "notistack";
import { MAX_DROPDOWN_HEIGHT } from "../../../types/micellinous-constants";

const CreatePatientDialog = ({ open, onClose, onPatientCreated }) => {
  const [units, setUnits] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [isRoomOccupied, setIsRoomOccupied] = useState(false);
  const [isNameConflict, setIsNameConflict] = useState(false);
  const [conflictingPatientName, setConflictingPatientName] = useState("");

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await getFacilities();
        if (response.status === 200 && response.data.status) {
          setFacilities(response.data.body);
        }
      } catch (error) {
        console.error("Error fetching units:", error);
      }
    };

    fetchUnits();
  }, []);

  const initialValues = {
    facility: "",
    unit: "",
    room: "",
    patient: "",
    aide: "",
    isolationStartDate: "",
  };

  const createPatient = async (patientData) => {
    try {
      const createResponse = await createPatientRecord(patientData);
      if (createResponse.status === 200) {
        onPatientCreated(createResponse.data);
        if (createResponse?.data?.status === true) {
          enqueueSnackbar("Patient Created!", { variant: "success" });
        } else {
          enqueueSnackbar("Something went wrong. Please try again", {
            variant: "error",
          });
        }
        onClose();
      }
    } catch (error) {
      console.error("Error creating patient record:", error);
      enqueueSnackbar("Failed to create patient.", { variant: "error" });
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    let roomOccupiedWarningShown = false;
    try {
      const response = await getPatientRecords();
      if (response.status === 200 && response.data.status) {
        const patientRecords = response.data.body;

        const enteredRoom = values.room.trim();
        const isOccupied = patientRecords.some(
          (record) => record.room.trim() === enteredRoom
        );

        if (isOccupied) {
          setIsRoomOccupied(true);
          roomOccupiedWarningShown = true;
        }

        const enteredPatientName = values.patient.trim().toLowerCase();
        const nameConflict = patientRecords.some(
          (record) => record.patient.trim().toLowerCase() === enteredPatientName
        );

        if (nameConflict) {
          setIsNameConflict(true);
          return;
        }
        // var dateTemp = new Date(values.isolationStartDate);
        createPatient({
          unit: values.unit,
          room: values.room,
          patient: values.patient,
          aide: values.aide,
          facility: values.facility,
          isolationStartDate: values.isolationStartDate,
        });
      } else {
        // does not exist
        createPatient({
          unit: values.unit,
          room: values.room,
          patient: values.patient,
          aide: values.aide,
          facility: values.facility,
        });
      }
    } catch (error) {
      console.error("Error during room check:", error);
    }
  };
  function getUnitsByName(name, data) {
    // Find the object with the specified name
    const foundObject = data.find((obj) => obj.name === name);

    // If the object is found, return its units; otherwise, return null
    return foundObject ? foundObject.units : null;
  }
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        className={styles.dialogStylesPatient}
      >
        <DialogTitle>Create New Patient</DialogTitle>
        <DialogContent>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            // validationSchema={patientSchema}
            enableReinitialize={true}
          >
            {({
              values,
              handleSubmit,
              isSubmitting,
              resetForm,
              setFieldValue,
            }) => (
              <Form>
                <div className={styles.flexContainer}>
                  <div className={styles.flexContainerColumnLeft}>
                    <div>
                      <p className={styles.patientLabelDetailStyles}>
                        Facility
                      </p>
                      <FormControl variant="outlined" fullWidth>
                        <Select
                          option={facilities}
                          defaultValue={null}
                          value={values.facility}
                          MenuProps={{
                            style: {
                              maxHeight: MAX_DROPDOWN_HEIGHT,
                            },
                          }}
                          className={clsx(
                            `${styles.dropdown} ${styles.flexLeft}`
                          )}
                          onChange={(event) => {
                            const selectedValue = event.target.value;
                            setFieldValue("facility", selectedValue);
                            setUnits(getUnitsByName(selectedValue, facilities));
                          }}
                        >
                          {facilities?.map((facility) => {
                            return (
                              <MenuItem
                                key={facility?.name}
                                value={facility?.name}
                                selected={facility?.name}
                                className={clsx(`${styles.flexLeft}`)}
                              >
                                {facility?.name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </div>
                    <div>
                      <p className={styles.patientLabelDetailStyles}>Unit</p>
                      <FormControl variant="outlined" fullWidth>
                        <Select
                          option={units}
                          defaultValue={null}
                          disabled={values.facility == ""}
                          value={values.unit}
                          MenuProps={{
                            style: {
                              maxHeight: MAX_DROPDOWN_HEIGHT,
                            },
                          }}
                          className={clsx(
                            `${styles.dropdown} ${styles.flexLeft}`
                          )}
                          onChange={(event) => {
                            const selectedValue = event.target.value;
                            setFieldValue("unit", selectedValue);
                          }}
                        >
                          {units?.map((unit) => {
                            return (
                              <MenuItem
                                key={unit?.id}
                                value={unit?.id}
                                selected={unit?.name}
                                className={clsx(`${styles.flexLeft}`)}
                              >
                                {unit?.name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </div>
                    <div>
                      <p
                        className={styles.patientLabelDetailStyles}
                        htmlFor="room"
                      >
                        Room:
                      </p>

                      <Field
                        as={TextField}
                        type="text"
                        name="room"
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                  </div>

                  <div className={styles.flexContainerColumnRight}>
                    <div>
                      <p
                        className={styles.patientLabelDetailStyles}
                        htmlFor="patient"
                      >
                        Patient:
                      </p>

                      <Field
                        as={TextField}
                        type="text"
                        name="patient"
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                    <div>
                      <p
                        className={styles.patientLabelDetailStyles}
                        htmlFor="aide"
                      >
                        Pathogen:
                      </p>

                      <Field
                        as={TextField}
                        type="text"
                        name="aide"
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                    <div>
                      <p
                        className={styles.patientLabelDetailStyles}
                        htmlFor="isolationStartDate"
                      >
                        Isolation Start Date:
                      </p>

                      <Field
                        as={TextField}
                        type="date"
                        name="isolationStartDate"
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
                <DialogActions className={styles.flexCenter}>
                  <Button
                    onClick={() => {
                      onClose();
                      resetForm();
                    }}
                    className={styles.redButton}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className={styles.primaryButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Creating..." : "Create"}
                  </Button>
                </DialogActions>
                <Dialog
                  open={isRoomOccupied}
                  onClose={() => setIsRoomOccupied(false)}
                  className={styles.dialogStylesPatient}
                >
                  <DialogTitle>Room Occupied</DialogTitle>
                  <DialogContent>
                    <Typography>
                      Note: The room you selected is occupied by another
                      patient. Click ok to proceed.
                    </Typography>
                  </DialogContent>
                  <DialogActions className={styles.flexCenter}>
                    <Button
                      onClick={() => setIsRoomOccupied(false)}
                      color="primary"
                    >
                      OK
                    </Button>
                  </DialogActions>
                </Dialog>
                <Dialog
                  open={isNameConflict}
                  onClose={() => setIsNameConflict(false)}
                  className={styles.dialogStylesPatient}
                >
                  <DialogTitle>Patient Name Conflict</DialogTitle>
                  <DialogContent>
                    <Typography>
                      A patient named '{conflictingPatientName}' already exists.
                      Do you want to proceed?
                    </Typography>
                  </DialogContent>
                  <DialogActions className={styles.flexCenter}>
                    <Button
                      onClick={() => setIsNameConflict(false)}
                      color="primary"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        setIsNameConflict(false);
                        createPatient({
                          unit: values.unit,
                          room: values.room,
                          patient: values.patient,
                          aide: values.aide,
                        });
                      }}
                      color="primary"
                    >
                      Proceed
                    </Button>
                  </DialogActions>
                </Dialog>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreatePatientDialog;
